<template>
	<v-expand-transition>
		<v-card
			v-if="visible && status"
			class="setup-card mb-4"
			:class="`setup-card--${accent}`"
			rounded="lg"
			border
			elevation="0"
		>
			<v-btn
				class="setup-card__dismiss"
				variant="text"
				size="small"
				icon="mdi-close"
				:aria-label="t('dashboard.setup.dismiss')"
				@click="dismiss"
			/>

			<div class="setup-card__body">
				<!-- Faço eu mesmo: progresso, etapas e a chamada do próximo passo. -->
				<section class="setup-card__self">
					<div class="d-flex align-center flex-wrap ga-2">
						<span class="d-flex align-center ga-2 font-weight-bold">
							<v-icon size="20" :color="accent">{{ headerIcon }}</v-icon>
							{{ t(titleKey) }}
						</span>
						<span class="text-caption text-medium-emphasis">{{
							t(progressKey, {
								done: progressDone,
								total: progressTotal,
							})
						}}</span>
					</div>
					<v-progress-linear
						class="mt-2"
						:model-value="progressPct"
						:color="accent"
						bg-color="grey-lighten-3"
						rounded
						height="6"
					/>

					<div class="d-flex flex-wrap ga-1 mt-2">
						<v-chip
							v-for="(item, index) in displayItems"
							:key="item.key"
							class="setup-card__chip"
							size="small"
							:color="item.done ? 'grey-darken-1' : undefined"
							:variant="item.done ? 'tonal' : 'outlined'"
							:prepend-icon="item.done ? 'mdi-check' : undefined"
							@click="openStep(item.key)"
						>
							<template v-if="!item.done">{{ index + 1 }}.&nbsp;</template>{{
								t(`dashboard.setup.items.${item.key}`)
							}}
						</v-chip>
					</div>

					<!-- Chamada separada e destacada: foi o que tirou o usuário do beco sem
					     saída no teste de usabilidade da v1. Rótulo trocado no chip passa batido. -->
					<div
						v-if="nextItem"
						data-testid="setup-next-step"
						class="setup-card__next mt-3 d-flex align-center ga-2"
					>
						<v-icon size="18" :color="accent">mdi-arrow-right-circle</v-icon>
						<!-- Abrir colado ao texto, não na ponta: com o assistente aberto em meia
						     tela, a ponta direita do card fica embaixo do painel. Com rótulo
						     longo em tela de 1440px ele ainda fica embaixo — o painel sobrepõe,
						     não empurra o dashboard. -->
						<span class="text-body-2 font-weight-medium">{{
							t("dashboard.setup.next", { item: nextLabel })
						}}</span>
						<v-btn
							v-if="getSetupRoute(nextItem.key)"
							:color="accent"
							variant="elevated"
							size="small"
							@click="openStep(nextItem.key)"
						>
							{{ t("dashboard.setup.open") }}
						</v-btn>
					</div>
				</section>

				<!-- Me ajude: alternativa em coluna própria, com botão vazado para não
				     competir com o Abrir. -->
				<section data-testid="setup-assistant" class="setup-card__assistant">
					<div class="d-flex align-start ga-2">
						<v-icon size="20" color="primary">mdi-robot-happy-outline</v-icon>
						<span class="text-body-2">{{ t("dashboard.setup.assistantHint") }}</span>
					</div>
					<v-btn
						class="mt-3"
						color="primary"
						variant="outlined"
						size="small"
						block
						@click="startGuided"
					>
						{{ t("dashboard.setup.cta") }}
					</v-btn>
				</section>
			</div>
		</v-card>
	</v-expand-transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import apiService from "@/services/api";
import { useAssistant } from "@/composables/useAssistant";
import { useEntityRefresh } from "@/composables/useEntityRefresh";
import { getSetupStatus, type SetupStatus } from "@/services/chatService";
import { getSetupRoute } from "@/config/setupChecklist";

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const assistant = useAssistant();

const visible = ref(false);
// Dispensar é definitivo para o usuário naquele terreiro: grava em
// `completedTours` (mesmo mecanismo do lembrete de MFA), com o id do terreiro
// na chave porque a conta pode dirigir mais de um. Só em memória, o card voltava
// a cada acesso ao dashboard.
const dismissKey = () =>
	`setupChecklistDismissed:${authStore.currentTemple?.id ?? ""}`;
// Lido na hora de decidir, não na montagem: o usuário pode chegar do `/auth/me`
// depois de o card montar.
const isDismissed = () =>
	authStore.user?.completedTours?.[dismissKey()] === true;
const status = ref<SetupStatus | null>(null);

const SEED_PROMPT =
	"Quero configurar os cadastros iniciais do meu terreiro com a sua ajuda, um a um. Comece verificando o que já existe e me guie pelos cadastros essenciais que faltam.";

const canManage = computed(() => authStore.isOwner || authStore.isAdmin);

// Fase opcional só entra depois de o essencial fechar (há ao menos 1 ritual).
const isOptional = computed(() => status.value?.phase === "optional");

// A cor do widget muda ao passar para os opcionais (essencial concluído).
const accent = computed(() => (isOptional.value ? "info" : "success"));
const headerIcon = computed(() =>
	isOptional.value ? "mdi-star-plus-outline" : "mdi-clipboard-check-outline",
);
const titleKey = computed(() =>
	isOptional.value ? "dashboard.setup.optionalTitle" : "dashboard.setup.title",
);

const progressKey = computed(() =>
	isOptional.value
		? "dashboard.setup.optionalProgress"
		: "dashboard.setup.progress",
);

const displayItems = computed(() =>
	isOptional.value
		? (status.value?.optionalItems ?? [])
		: (status.value?.items ?? []),
);
const progressDone = computed(() =>
	isOptional.value
		? (status.value?.optionalDone ?? 0)
		: (status.value?.done ?? 0),
);
const progressTotal = computed(() =>
	isOptional.value
		? (status.value?.optionalTotal ?? 0)
		: (status.value?.total ?? 0),
);

const progressPct = computed(() =>
	progressTotal.value > 0
		? Math.round((progressDone.value / progressTotal.value) * 100)
		: 0,
);

const nextItem = computed(
	() => displayItems.value.find((item) => !item.done) ?? null,
);

// Tipo de Ritual só conta como feito com atendimento vinculado. Se já existe
// tipo criado, o que falta é o vínculo — e é isso que a chamada precisa dizer.
const nextLabel = computed(() => {
	const item = nextItem.value;
	if (!item) return "";
	if (item.key === "tipo_ritual" && item.count > 0) {
		return t("dashboard.setup.items.vincular_atendimento");
	}
	return t(`dashboard.setup.items.${item.key}`);
});

const dismiss = async () => {
	visible.value = false;
	const tourKey = dismissKey();
	if (authStore.user) {
		authStore.user.completedTours = {
			...authStore.user.completedTours,
			[tourKey]: true,
		};
	}
	try {
		const result = await apiService.patch("/auth/tours", {
			tourKey,
			completed: true,
		});
		if (authStore.user) authStore.user.completedTours = result.completedTours;
	} catch (err) {
		// O card já sumiu nesta sessão; só não fica gravado para o próximo acesso.
		console.error("Erro ao salvar dispensa do card de configuração:", err);
	}
};

const openStep = (key: string) => {
	const route = getSetupRoute(key);
	if (route) router.push(route);
};

// A pergunta do modo é feita pela TELA, não pelo modelo: visto em HML
// (18/09/2026), num terreiro quase configurado ele pulava a pergunta e os
// botões não apareciam. O clique já manda o modo com o nome que o roteiro usa.
const startGuided = () => {
	assistant.askLocally(
		{
			text: t("chat.guidedMode.question"),
			options: [
				{
					label: t("chat.guidedMode.automatic"),
					message: `${SEED_PROMPT} Prefiro o modo Automático.`,
				},
				{
					label: t("chat.guidedMode.manual"),
					message: `${SEED_PROMPT} Prefiro o modo Manual.`,
				},
			],
			typedAnswerPrefix: `${SEED_PROMPT} Sobre o modo (Automático ou Manual), respondi:`,
		},
		{ size: "large" },
	);
};

const loadStatus = async () => {
	// Só Dono/Admin (quem pode criar cadastros) e após o onboarding.
	if (!canManage.value) return;
	if (!authStore.currentTemple?.onboardingCompleted) return;
	if (isDismissed()) return;
	try {
		const data = await getSetupStatus();
		status.value = data;
		// Também ESCONDE quando fecha tudo: com a recarga automática, o último
		// cadastro criado ao lado tem de apagar o card, não só deixá-lo parado.
		visible.value = !isDismissed() && !data.allDone;
	} catch {
		// silencioso: card é auxiliar
	}
};

onMounted(loadStatus);

// O assistente cria ao lado, com o card em meia tela: o progresso e o "Próximo
// passo" precisam acompanhar sem recarregar a página. Dispensado, fica quieto.
useEntityRefresh().onEntityChanged(
	[
		"person",
		"spiritualLine",
		"guide",
		"attendanceType",
		"ritualType",
		"attendanceCard",
		"orixa",
		"function",
		"journeyStage",
	],
	() => {
		if (visible.value) loadStatus();
	},
);
</script>

<style scoped>
.setup-card {
	position: relative;
	border-inline-start: 4px solid rgb(var(--v-theme-success)) !important;
}

.setup-card--info {
	border-inline-start-color: rgb(var(--v-theme-info)) !important;
}

.setup-card__dismiss {
	position: absolute;
	top: 4px;
	right: 4px;
}

.setup-card__body {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
}

.setup-card__self {
	padding: 12px 16px;
}

.setup-card__assistant {
	padding: 40px 16px 12px;
	border-inline-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	background: rgba(var(--v-theme-primary), 0.04);
}

.setup-card__next {
	padding: 8px 10px;
	border-radius: 8px;
	background: rgba(var(--v-theme-on-surface), 0.04);
}

/* Celular: a coluna do assistente desce para baixo do próximo passo. */
@media (max-width: 700px) {
	.setup-card__body {
		grid-template-columns: minmax(0, 1fr);
	}

	.setup-card__self {
		padding-inline-end: 44px;
	}

	.setup-card__assistant {
		padding-top: 12px;
		border-inline-start: none;
		border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	}
}
</style>
